import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from 'api/api';
import TokenUtils from 'utils/TokenUtils';
import { addNotification } from './notificationsSlice';

export const getGroups = createAsyncThunk('groups/getGroups', async (params, { rejectWithValue }) => {
  try {
    const response = await api.get('/groups', {
      params: {
        name: params?.search,
        locationIDs: params?.locationIDs,
        expand: params?.expand,
      },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати групи');
  }
});

export const getGroup = createAsyncThunk('groups/getGroup', async (groupID, { rejectWithValue }) => {
  try {
    const response = await api.get(`/groups/${groupID}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати дані групи');
  }
});

export const createGroup = createAsyncThunk('groups/createGroup', async (groupData, { dispatch, rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    const response = await api.post('/groups', groupData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    dispatch(
      addNotification({
        id: Date.now(),
        title: 'Групу успішно створено',
        type: 'success',
      })
    );

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Помилка створення групи');
  }
});

export const updateGroup = createAsyncThunk(
  'groups/updateGroup',
  async ({ id, ...updateData }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.put(`/groups/${id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Групу успішно оновлено',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка оновлення групи');
    }
  }
);

export const deleteGroups = createAsyncThunk('groups/deleteGroups', async (groupIDs, { dispatch, rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    await api.delete(`/groups`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: { ids: groupIDs },
    });

    dispatch(
      addNotification({
        id: Date.now(),
        title: 'Групи успішно видалені',
        type: 'success',
      })
    );

    return groupIDs;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Помилка видалення групи');
  }
});

export const addClientsToGroup = createAsyncThunk(
  'groups/addClients',
  async ({ groupID, clientIDs }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.post(
        `/groups/${groupID}/clients`,
        { clientIDs: [...clientIDs] },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Клієнтів додано до групи',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка додавання клієнтів');
    }
  }
);

export const removeMemberFromGroup = createAsyncThunk(
  'groups/removeMember',
  async ({ groupID, clientID }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.delete(`/groups/${groupID}/clients`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { clientID },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Клієнта видалено з групи',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка видалення клієнта');
    }
  }
);

const groupsSlice = createSlice({
  name: 'groups',
  initialState: {
    groups: [],
    meta: {
      total: 0,
      page: 1,
      limit: 100,
      totalPages: 1,
    },
    error: null,
    isLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getGroups.fulfilled, (state, action) => {
        state.groups = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(getGroups.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(getGroup.fulfilled, (state, action) => {
        const existingIndex = state.groups.findIndex((g) => g.id === action.payload.id);

        if (existingIndex !== -1) {
          state.groups[existingIndex] = action.payload;
        } else {
          state.groups.unshift(action.payload);
        }
      })
      .addCase(getGroup.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати дані групи';
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.groups.unshift(action.payload);
      })
      .addCase(createGroup.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        const index = state.groups.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
      })
      .addCase(updateGroup.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteGroups.fulfilled, (state, action) => {
        state.groups = state.groups.filter((group) => !action.payload.includes(group.id));
      })
      .addCase(deleteGroups.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(addClientsToGroup.fulfilled, (state, action) => {
        const index = state.groups.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
      })
      .addCase(addClientsToGroup.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(removeMemberFromGroup.fulfilled, (state, action) => {
        const index = state.groups.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
      })
      .addCase(removeMemberFromGroup.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addMatcher(
        (action) => action.type.startsWith('groups/') && action.type.endsWith('/pending'),
        (state) => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) =>
          action.type.startsWith('groups/') &&
          (action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected')),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export default groupsSlice.reducer;
